import { computed, ref, type ComputedRef } from 'vue';
import { FETCH_PARENT_OPEN_JOBS } from '@/graphql/messages/queries';
import { allowedJobCareTypesForSearchCareType } from '@/helpers/allowedJobCareTypes';
import type { JobEdge, ViewerJobs } from '@/graphql/messages/queries';

export type RecruitApollo = {
  query: (options: { query: unknown }) => Promise<{ data: { viewer: ViewerJobs } }>;
  mutate: (options: { mutation: unknown; variables: Record<string, unknown> }) => Promise<unknown>;
};

const DONT_SEND_JOB = "Don't send job";

export function useRecruitJobs(options: {
  jobs: ComputedRef<JobEdge[] | null>;
  searchCareType: ComputedRef<string>;
  isBasic: ComputedRef<boolean>;
  getClient: () => RecruitApollo;
}) {
  const viewer = ref<ViewerJobs>({} as ViewerJobs);
  const selectedJob = ref('');

  const hasJobsProp = computed(() => options.jobs.value !== null && options.jobs.value.length > 0);
  const filteredJobEdges = computed(() => {
    const jobEdges = hasJobsProp.value ? options.jobs.value || [] : viewer.value?.jobs?.edges || [];
    const allowed = allowedJobCareTypesForSearchCareType(options.searchCareType.value);
    if (allowed.length === 0) {
      return jobEdges;
    }
    return jobEdges.filter((edge) => edge?.node?.careType && allowed.includes(edge.node.careType));
  });
  const noJobsExist = computed(() => filteredJobEdges.value.length === 0);
  const jobOptions = computed(() => {
    const formatted = filteredJobEdges.value.map(({ node }) => ({ display: node.title, value: node.id }));
    if (!options.isBasic.value) {
      return [...formatted, { display: DONT_SEND_JOB, value: DONT_SEND_JOB }];
    }
    return formatted;
  });
  const selectedJobUuid = computed(() => (selectedJob.value === DONT_SEND_JOB ? null : selectedJob.value));

  async function prepareJobs(): Promise<void> {
    if (!hasJobsProp.value) {
      await fetchJobs();
      return;
    }
    selectedJob.value = filteredJobEdges.value[0]?.node?.id || '';
  }

  async function fetchJobs(): Promise<void> {
    if (hasJobsProp.value) {
      return;
    }
    try {
      const { data } = await options.getClient().query({ query: FETCH_PARENT_OPEN_JOBS });
      viewer.value = data.viewer;
      selectedJob.value = filteredJobEdges.value[0]?.node?.id || '';
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  }

  return { selectedJob, noJobsExist, jobOptions, selectedJobUuid, prepareJobs };
}
